'use client';

import { twBreakpoints } from '@woonstad/design-system';
import { useMediaQuery } from '@woonstad/helpers-hooks';
import { FilterBar, getDictionary, HeadingFilter, Main, Providers, SITES, useLocationSearchParams, useOverviewStore } from '@woonstad/ui-public-sites';
import { Lang } from 'apps/public-sites/www-stadswonen/types/PageProps';
import { Suspense, useEffect, useMemo } from 'react';

import { useWoongebouwen } from './use-woongebouwen';
import { WoongebouwItem } from './woongebouw-item';

type Props = {
  lang: Lang;
  baseUrl: string;
  umbracoBaseUrl: string;
}

const OverviewInner = ({
  lang,
  baseUrl,
  umbracoBaseUrl,
}: Props) => {
  useWoongebouwen(baseUrl);

  const setOverviewItemSlot = useOverviewStore((state) => state.setOverviewItemSlot);
  const setHasMap = useOverviewStore((state) => state.setHasMap);

  useEffect(() => {
    setOverviewItemSlot(WoongebouwItem);
  }, [setOverviewItemSlot]);

  const setDictionary = useOverviewStore((state) => state.setDictionary);
  const dict = useMemo(() => getDictionary(SITES.Stadswonen), []);

  const searchParams = useLocationSearchParams();
  const isMapDialogOpen = searchParams.get('map-open') === 'true';

  useEffect(() => {
    setDictionary({
      pageTitle: dict['Woongebouwen'],
      noResultsIntro: dict['Geen woongebouwen gevonden'],
      noResultsAction1: dict['Pas de filters aan, of'],
      noResultsAction2: dict['wis de filters'],
      noResultsAction3: dict['om alle woongebouwen te zien.'],
      noResultsNow: dict['Er zijn op dit moment helaas geen woongebouwen beschikbaar.'],
      searchPlaceholder: 'Zoek op woongebouw',
    });
  }, [setDictionary, dict]);

  const setTrackingDetails = useOverviewStore((state) => state.setTrackingDetails);
  const setHasSort = useOverviewStore((state) => state.setHasSort);

  useEffect(() => {
    setTrackingDetails({
      clearFiltersLocation: 'Woongebouw overzichtspagina',
      filterUsageLocation: 'Woongebouw overzichtspagina',
    });

    setHasSort(false);
    setHasMap(false);
  }, [setTrackingDetails, setHasSort, setHasMap]);

  const isMobile = useMediaQuery(twBreakpoints['max-md']);

  return (
    <div className="grid gap-10 md:grid-cols-12">
      <Main
        umbracoBaseUrl={umbracoBaseUrl}
        site={SITES.Stadswonen}
        testid="aanbod"
      />

      {!isMobile && !isMapDialogOpen && (
        <aside className="mt-20 flex h-fit flex-col gap-7 md:col-span-4">
          <HeadingFilter site={SITES.Stadswonen} />
          <FilterBar />
        </aside>
      )}
    </div>
  );
};

export const Overview = (props: Props) => {
  return (
    <Suspense fallback={null}>
      <Providers>
        <OverviewInner {...props} />
      </Providers>
    </Suspense>
  );
};
